import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ExpenseCodeContainer from '../models/ExpenseCodeContainer';
import Expense from './Expense';
import DayEntry from 'src/models/AP/DayEntry';
import Receipt from 'src/models/AP/EmployeeExpenseReceipt';
import { useServiceCtx } from './TimeAndExpenseServiceContext';

interface IExpenseData {
  category: string;
  description: string;
  receipt: Receipt;
}

interface IExpenseTableProps {
  expenses?: IExpenseData[];
  addExpense?: boolean;
  entryIndex: string;
  indexSelected?: string;
  dayEntry: DayEntry;
  dayEntryIndex?: number;
  calendarDayIndex?: number;
  tmId?: number;
  register?: ReturnType<typeof useForm>['register'];
  setValue?: ReturnType<typeof useForm>['setValue'];
  expenseAdded: (signal: boolean) => void;
  disableExpense?: boolean;
  displayReceipts: (
    flag: boolean,
    esId?: number,
    expenseCount?: number
  ) => void;
  showContainer?: (flag: boolean) => void;
  adminToUser?: boolean;
  payperiod?: string;
}

const ExpenseTable = ({
  addExpense,
  entryIndex,
  indexSelected,
  dayEntry,
  dayEntryIndex,
  calendarDayIndex,
  tmId,
  register,
  setValue,
  expenseAdded,
  disableExpense,
  displayReceipts,
  showContainer,
  adminToUser,
  payperiod,
}: IExpenseTableProps) => {
  // state from ServiceCtx
  const { serviceState } = useServiceCtx();

  // State variables
  const [updateReceipts, setUpdateReceipts] = useState<{ desc: string, calendarDayIndex: number, count: number}>(undefined);
  const [expenseCodes, setExpenseCodes] = useState<Array<ExpenseCodeContainer>>([]);
  const [expand, setExpand] = useState(false);

  useEffect(() => {
    setUpdateReceipts(undefined);
    setExpenseCodes(serviceState.expenseCodes);
  }, []);

  useEffect(() => {
    if (!!serviceState.expenseCodes) {
      setExpenseCodes(serviceState.expenseCodes);
    }
  }, [serviceState.expenseCodes]);
  
  useEffect(() => {
    if (!!entryIndex && !!indexSelected && entryIndex === indexSelected) {
      setExpand(true);
      expenseAdded(true);
    }
  }, [entryIndex, indexSelected, addExpense]);

  const inputNameCorrection = (name: string) => {
    switch (name) {
      case 'abstract fees':
        return 'abstractFees';
      case 'shipping expenses':
        return 'shipping';
      case 'toll road':
        return 'tolls';
      case 'recording fees':
        return 'recordingFees';
      case 'tax certificates':
        return 'taxCerts';
      case 'forms & supplies':
        return 'formSupplies';
      case 'flat rate expense':
        return 'flatRate';
      case 'lodging per diem':
        return 'lodging';
      case 'online subscriptions':
        return 'onlineSubscriptions';
      default:
        return name;
    }
  };

  const tooltipContent = (name: string) => {
    switch (name) {
      case 'mileage':
        return 'Miles driven to and from job site,\nor other driving pertaining to project only';
      case 'meals':
        return 'Alloted meal reimbursement\nbased on client approval.\n*Must be billed with mileage\nand/or hotel/lodging*';
      case 'motel':
        return 'Hotel expenses during approved travel.\n*Must be billed nightly';
      case 'copies':
        return 'Fees pertaining to document viewing,\nreproduction, or printing';
      case 'abstractFees':
        return 'Fees pertaining to abstracting';
      case 'tolls':
        return 'Info about the expense';
      case 'recordingFees':
        return 'Any fees in relation to filing documents\nwith a county or other applicable entity';
      case 'taxCerts':
        return 'Any fees in relation to tax certificates';
      case 'formSupplies':
        return 'Office supply expenses approved\nor requested by client or DLC\n(ex: envelopes, paper clips, etc.)';
      case 'flatRate':
        return 'Allotted per diem amount\nin addition to the day rate\n*Flat Rates must be approved\nand provided by the client.\nMost clients do not provide flat rates.*';
      case 'lodging':
        return `Any accomodations for jobsite travel\nthat are not a hotel.\n(ex: B&B's, Camping, RV, Airbnb, VRBO, etc.)`;
      case 'postage':
        return 'Any postage fees to be billed to clients\n (USPS, stamps, certified mail, etc)';
      case 'airfare':
        return 'Airfare for approved travel\nby plane to jobsites';
      case 'notary':
        return 'Fees/expenses related to notary services';
      case 'parking':
        return 'Any parking expenses incurred\nduring approved business travel';
      case 'shipping':
        return 'Any shipping fees to be billed to clients\n(FedEx, UPS, etc.)';
      case 'transportation':
        return 'Transortation fees\nfor approved travel to jobsites\n(Uber, Lyft, Taxis, Car Rentals, Tolls, etc.)';
      case 'other':
        return 'Miscellaneous expenses that do not fit\nin any of the existing expense categories ';
      case 'online subscriptions':
        return 'All online subscriptions or access fees.\nThis includes but is not limited to:\nOKCountyRecords, CourthouseDirect,\nBeen Verified, CountyRecords,\nDropbox, and any\nCounty/County Clerk Online Search\nor Access Fees.';
      case 'emailReceipt':
        return 'Email this receipt.';
      default:
        return 'Will Fix when data comes back!';
    }
  };

  const renderExpenses = () => {
    return !!expenseCodes && expenseCodes?.length! > 0 ? (
      expenseCodes?.map((e, index) => {
        let correctInputName = inputNameCorrection(
          e?.description.toLowerCase()
        );
        let inputName = `expenses.${calendarDayIndex}.${dayEntryIndex}.${correctInputName}`;
        return (
          <div className="tw-col-span-4" key={index}>
            <Expense
              register={register!}
              setValue={setValue!}
              expenseLabel={e?.description}
              expenseCode={e?.code}
              inputName={inputName}
              otherInputName={`expenses.${calendarDayIndex}.${dayEntryIndex}`}
              dayEntryIndex={dayEntryIndex}
              dayEntry={dayEntry}
              index={index}
              expenseCount={
                !!updateReceipts &&
                updateReceipts?.desc === e?.description &&
                updateReceipts?.calendarDayIndex ===
                  calendarDayIndex &&
                updateReceipts?.count
              }
              calendarDayIndex={calendarDayIndex!}
              disableExpense={disableExpense}
              displayReceitps={displayReceipts}
              tooltipContent={tooltipContent(correctInputName)}
              adminToUser={adminToUser}
              payperiod={payperiod}
            />
          </div>
        );
      })
    ) : (
      <div className="tw-col-span-3 tw-w-full tw-items-center">
        <p>No Expenses Available</p>
      </div>
    );
  };

  return (
    <div
      className={
        !!expand
          ? 'tw-grid tw-grid-cols-[175px_125px_125px_125px_50px] tw-justify-center tw-items-center tw-p-[0.5rem_1rem_0.5rem_6rem] tw-border-gray-300 tw-border-l-[1px] tw-border-r-[1px] tw-border-b-[1px] tw-rounded tw-overflow-x-hidden tw-overflow-y-scroll h-[45vh]'
          : 'tw-hidden tw-opacity-0'
      }
    >
      <div className="tw-col-span-5 tw-w-full tw-mb-4">
        <div className="tw-flex tw-justify-center tw-gap-[6rem]">
          <h3>Expense</h3>
          <h3>Amount</h3>
          <h3>Files</h3>
          <div className="tw-w-[10px]" />
        </div>
      </div>
      {renderExpenses()}
      <div className="tw-col-span-2 tw-mt-4">
        <button
          onClick={() => {
            setExpand(false);
            showContainer!(false);
          }}
          className="tw-bg-green-500 tw-text-white tw-hover:tw-bg-green-600 tw-rounded tw-w-1/2 tw-p-[2px]"
        >
          Collapse
        </button>
      </div>
    </div>
  );
};

export default ExpenseTable;


// WEBPACK FOOTER //
// ./src/components/project-billing/components/ExpenseTable.tsx